/**
 * Phase 32a — Live travel preview for booking create/edit.
 *
 * Renders TWO blocks (from-previous + to-next) for the proposed booking.
 * The preview is fetched from `POST /api/bookings/travel-preview` and
 * debounced to 500ms after the inputs (staff, date, time, location) stop
 * changing. Soft warning rendering only — saving is never blocked.
 *
 * Props:
 *   staffId           : string  — required to trigger fetch
 *   scheduledStartIso : string  — ISO datetime in UTC
 *   durationMinutes   : number
 *   location          : { postcode, address_line, lat?, lng? }
 *   excludeBookingId  : string? — set in edit mode so the booking being
 *                                 edited isn't matched as its own neighbour
 *   className         : string?
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowUpRight, AlertTriangle } from "lucide-react";

const DEBOUNCE_MS = 500;

function Block({ kind, block }) {
  if (!block) return null;
  const warn = !!block.warning_flag;
  const labelKind = kind === "from_previous" ? "From previous job" : "To next job";
  const arrow = "↗";
  const minutes = block.travel_minutes != null ? Math.round(Number(block.travel_minutes)) : null;
  const km = block.distance_km != null ? Number(block.distance_km).toFixed(1) : null;
  const tip = warn
    ? `Tight — staff has only ${block.gap_minutes ?? "?"} min after ${
        kind === "from_previous" ? "previous job" : "this job"
      } for a ${minutes ?? "?"} min trip.`
    : `${block.address || "—"} · ${block.booking_code}`;

  const headerTimeLabel =
    kind === "from_previous"
      ? `ending at ${block.ends_at || "—"}`
      : `starting at ${block.starts_at || "—"}`;
  const computedLabel =
    kind === "from_previous"
      ? block.arrival_time
        ? `Staff arrives at ${block.arrival_time}.`
        : ""
      : block.must_leave_by
      ? `Must leave by ${block.must_leave_by}.`
      : "";

  return (
    <div
      className={`flex items-start gap-2 rounded-[6px] border px-3 py-2 text-[12px] leading-snug ${
        warn
          ? "border-rose-300 bg-rose-50 text-rose-800"
          : "border-sky-200 bg-sky-50 text-sky-900"
      }`}
      data-testid={`travel-preview-${kind.replace("_", "-")}`}
      data-warning={warn ? "true" : "false"}
      title={tip}
    >
      <span aria-hidden="true" className="mt-0.5 text-base">
        {warn ? <AlertTriangle className="h-4 w-4 text-rose-600" /> : <ArrowUpRight className="h-4 w-4" />}
      </span>
      <div className="flex-1 min-w-0">
        <div className="mono text-[10px] uppercase tracking-wider">
          {arrow} {labelKind}
        </div>
        <div className="font-medium truncate">
          <span className="font-bold">{block.customer_name}</span> {headerTimeLabel}
          {block.address ? <span className="text-muted-foreground"> · {block.address}</span> : null}
        </div>
        <div className="mt-1 text-[11px]">
          {minutes != null && km != null ? (
            <span className="mono">
              <strong>{minutes} min · {km} km</strong>
              {computedLabel ? <span className="ml-2">{computedLabel}</span> : null}
            </span>
          ) : (
            <span className="text-muted-foreground italic">
              Travel time unavailable {block.address ? "(address not geocoded)" : ""}
            </span>
          )}
          {warn && (
            <div className="mt-1 text-rose-700 font-medium" data-testid={`travel-preview-warning-${kind.replace("_", "-")}`}>
              ⚠ Tight buffer — only {Math.round(Number(block.gap_minutes || 0))} min gap
              {minutes != null ? ` for a ${minutes} min trip` : ""}.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function TravelPreview({
  staffId,
  scheduledStartIso,
  durationMinutes,
  location,
  excludeBookingId,
  className = "",
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Derive a stable signature so we only re-fire on real input changes.
  const sig = JSON.stringify({
    staffId: staffId || "",
    scheduledStartIso: scheduledStartIso || "",
    durationMinutes: durationMinutes || 0,
    postcode: location?.postcode || "",
    address_line: location?.address_line || "",
    lat: location?.lat ?? null,
    lng: location?.lng ?? null,
    excludeBookingId: excludeBookingId || "",
  });

  useEffect(() => {
    if (!staffId || !scheduledStartIso) {
      setData(null);
      return;
    }
    if (!location || !(location.postcode || location.address_line || (location.lat && location.lng))) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data: resp } = await api.post("/bookings/travel-preview", {
          staff_id: staffId,
          scheduled_start: scheduledStartIso,
          duration_minutes: durationMinutes || 60,
          location: {
            postcode: location.postcode || "",
            address_line: location.address_line || "",
            lat: location.lat ?? null,
            lng: location.lng ?? null,
          },
          exclude_booking_id: excludeBookingId || null,
        });
        if (!cancelled) setData(resp);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  if (!staffId || !scheduledStartIso) return null;
  if (!data || (!data.from_previous && !data.to_next)) {
    if (loading) {
      return (
        <div
          className={`text-[11px] italic text-muted-foreground ${className}`}
          data-testid="travel-preview-loading"
        >
          Checking travel between same-day jobs…
        </div>
      );
    }
    return null;
  }

  return (
    <div
      className={`space-y-2 ${className}`}
      data-testid="travel-preview"
      data-ors-available={data.ors_available ? "true" : "false"}
    >
      <Block kind="from_previous" block={data.from_previous} />
      <Block kind="to_next" block={data.to_next} />
    </div>
  );
}

export default TravelPreview;
